import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Header from './shared/Header';
import Footer from './shared/Footer';
import {
  Bot,
  BookOpen,
  GitBranch,
  Database,
  ArrowRight,
  CheckCircle,
} from 'lucide-react';

const services = [
  {
    id: 'claude',
    icon: <Bot size={28} />,
    headline: 'Claude Implementation & Integration',
    description:
      'We stand up Claude in production, integrated into your existing products, internal tools, or customer-facing applications. From first API call to production deployment, we handle the engineering and the judgment calls.',
    deliverables: [
      'Production Claude deployments integrated with your stack',
      'Prompt engineering, evals, and guardrails',
      'Integration with existing products, internal tools, and customer-facing apps',
    ],
  },
  {
    id: 'rag',
    icon: <BookOpen size={28} />,
    headline: 'RAG & Knowledge Systems',
    description:
      'Your data is proprietary. We build retrieval systems that let your AI work with it: accurately, at scale, without hallucinating. From chunking strategy to hybrid search, we own the full pipeline.',
    deliverables: [
      'Custom retrieval over proprietary data sources',
      'Vector databases, embedding pipelines, hybrid search',
      'Document ingestion, chunking, and indexing strategies',
    ],
  },
  {
    id: 'agents',
    icon: <GitBranch size={28} />,
    headline: 'Agentic Workflows & Automation',
    description:
      'We design and ship multi-step agent systems that replace manual processes and unlock net-new capabilities. Tool use, orchestration, error handling: the full engineering picture, not a prototype.',
    deliverables: [
      'Multi-step agent systems for internal operations',
      'Tool use, function calling, and agent orchestration',
      'Workflow automation that replaces manual processes',
    ],
  },
  {
    id: 'data',
    icon: <Database size={28} />,
    headline: 'Data Infrastructure & Analytics',
    description:
      'Modern data stacks that actually move data where it needs to go. We architect, build, and operate pipelines for teams that have outgrown their current setup. DataBackfill Sync, our own GA4 to BigQuery product, is a live example of this work.',
    deliverables: [
      'GA4, BigQuery, and data warehouse architecture',
      'ETL and reverse-ETL pipelines',
      'DataBackfill Sync as a productized reference implementation',
    ],
  },
];

const ServicesPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hash]);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Page header */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-gradient-to-br from-blue-50 via-white to-white">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm font-medium mb-6">
            What we build
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-6 max-w-2xl leading-tight">
            Services
          </h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl leading-relaxed">
            Four practice areas. All of them end with working software in production.
          </p>
        </div>
      </section>

      {/* Service sections */}
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 space-y-20 md:space-y-28">
        {services.map((service, index) => (
          <section key={service.id} id={service.id} className="scroll-mt-24">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 items-start ${
              index % 2 === 1 ? 'lg:grid-flow-dense' : ''
            }`}>
              {/* Text side */}
              <div className={index % 2 === 1 ? 'lg:col-start-2' : ''}>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center mb-6 text-blue-600">
                  {service.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                  {service.headline}
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8 text-base md:text-lg">
                  {service.description}
                </p>
                <Link
                  to="/contact"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="inline-flex items-center px-5 py-2.5 bg-blue-600 hover:bg-blue-700
                             text-white rounded-lg font-medium transition-colors text-sm"
                >
                  Talk to us
                  <ArrowRight className="ml-2" size={16} />
                </Link>
              </div>

              {/* Deliverables card */}
              <div className={`bg-gray-50 rounded-2xl p-8 border border-gray-100 ${
                index % 2 === 1 ? 'lg:col-start-1 lg:row-start-1' : ''
              }`}>
                <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-6">
                  What you get
                </h3>
                <ul className="space-y-4">
                  {service.deliverables.map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle size={18} className="text-blue-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm leading-relaxed">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Divider between sections */}
            {index < services.length - 1 && (
              <div className="border-b border-gray-100 mt-20 md:mt-28" />
            )}
          </section>
        ))}
      </div>

      {/* Bottom CTA */}
      <section className="py-12 md:py-20 bg-gray-50">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-lg text-gray-700 mb-6">
            Not sure which fits? Book a discovery call.
          </p>
          <Link
            to="/contact"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="inline-flex items-center px-6 py-3 bg-blue-600 hover:bg-blue-700
                       text-white rounded-xl font-medium transition-all shadow-md hover:shadow-lg"
          >
            Start the conversation
            <ArrowRight className="ml-2" size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
